import { inject } from "mobx-react";
import { useEffect, useState, useCallback, useRef } from "react";
import classnames from "classnames";
import type { BlockFragment } from "@ihr-radioedit/inferno-webapi";
import { isYouTubeChannel, isYouTubePlaylist } from "../../lib/guards";
import { isWindowDefined } from "../../lib/window";
import { FauxButton } from "../../ui";
import { ButtonKind } from "../../ui/Button.component";
import { formatImage } from "@ihr-radioedit/inferno-core";
import styles from "./YouTubePlayer.module.scss";
import { Store } from "../../../../client/stores";

export interface VideoItem {
    id: string;
    snippet: {
        title: string;
        thumbnails: {
            default: {
                url: string;
            };
        };
        videoId: {
            id: string;
        };
    };
}

export interface YouTubePlayerProps {
    store?: Store;
    block: BlockFragment;
}

export const YouTubePlayer = inject("store")(({ block, store }: YouTubePlayerProps) => {
    if (!store || store.disableThirdParty || !isYouTubePlaylist(block) || !isYouTubeChannel(block)) {
        return null;
    }

    const { site, env } = store;
    if (!site.sections.design?.station_logo || !site.sections.general) {
        return null;
    }
    const stationLogo = formatImage(site.sections.design.station_logo.id, env);
    const [playerVideoId, setVideoId] = useState("");
    const [videoTitle, setVideoTitle] = useState("");
    const [videoList, setVideoList] = useState([]);
    const [youtubeLink, setYoutubeLink] = useState("");
    const playerWrapper = useRef<HTMLDivElement | null>(null);

    const initPlaylist = useCallback((playlist, items, video, title) => {
        setVideoId(video.id);
        setVideoTitle(title);
        setVideoList(items);
        setYoutubeLink(`playlist?list=${playlist.id}`);
    }, []);

    const initChannel = useCallback((channel, items, video, title) => {
        setVideoId(video.id);
        setVideoTitle(title);
        setVideoList(items);
        setYoutubeLink(`channel/${channel.id}`);
    }, []);

    useEffect(() => {
        if (isWindowDefined()) {
            if (!!block.playlist) {
                const { playlist } = block;
                const { items } = playlist?.youtube?.playlist?.playlistItems ?? { items: [] };
                const { snippet } = items[0] ?? null;
                const { videoId, title } = snippet ?? { videoId: null, title: null };
                initPlaylist(playlist, items, videoId, title);
            }

            if (!!block.channel) {
                const { channel } = block;
                const { items } = channel.youtube?.channel?.uploadsPlaylist?.youtube?.playlist?.playlistItems ?? {
                    items: [],
                };
                const { snippet } = items[0] ?? null;
                const { videoId, title } = snippet ?? { videoId: null, title: null };
                initChannel(channel, items, videoId, title);
            }
        }
    }, [block, initPlaylist, initChannel]);

    const changeVideo = (newVideoId: any, newVideoTitle: string) => {
        setVideoId(newVideoId);
        setVideoTitle(newVideoTitle);

        const page = store?.page.currentPage;
        if (page && isWindowDefined()) {
            const referrer = window.location.href;

            store?.onAnalyticsAction.dispatch({
                sectionName: "youtube",
                pageName: page.name,
                context: `${newVideoTitle}`,
                action: "item_selected",
                url: "",
                referrer,
            });
        }
    };

    const videoClass = classnames(styles.video, {
        [styles.max6]: !!playerWrapper.current && playerWrapper.current.clientWidth >= 1060,
        [styles.max8]: !!playerWrapper.current && playerWrapper.current.clientWidth < 1060,
    });

    const playlistWrapperClass = classnames(styles.playlistWrapper, {
        [styles.wideColumn]: !!playerWrapper.current && playerWrapper.current.clientWidth >= 1060,
    });

    return (
        <div className={styles.youtubePlayer} ref={playerWrapper}>
            <div className={styles.wrapper} style={{ borderTop: "none" }}>
                <header>
                    <h2 className={styles.blockTitle}>{block.value.title}</h2>
                </header>
                <div className={videoClass}>
                    {!!playerVideoId ? (
                        <>
                            <div className="embed-responsive embed-responsive-16by9 media-asset">
                                <iframe
                                    title="YouTubePlayer"
                                    id="player"
                                    width="610"
                                    height="343"
                                    allowFullScreen
                                    src={`https://www.youtube.com/embed/${playerVideoId}?feature=oembed%22vq=hd1080%22frameborder="0"%22allow="accelerometer;autoplay;encrypted-media;gyroscope;picture-in-picture"`}
                                />
                            </div>
                            <h3 className={styles.videoTitle}>{videoTitle}</h3>
                        </>
                    ) : (
                        <div>Getting video...</div>
                    )}
                </div>
                <div className={playlistWrapperClass}>
                    <ul className={styles.playlist}>
                        {!!videoList &&
                            videoList.map((video: VideoItem, index: number) => (
                                <li
                                    key={video.id}
                                    className={`${styles.thumbnail} ${
                                        video.snippet.videoId.id === playerVideoId ? styles.active : ""
                                    }`}
                                    onClick={() => changeVideo(video.snippet.videoId.id, video.snippet.title)}
                                    tabIndex={-Math.abs(index)}
                                >
                                    {!!video.snippet.thumbnails.default ? (
                                        <img
                                            src={video.snippet.thumbnails.default.url}
                                            alt={video.snippet.title + "-thumbnail"}
                                            className={styles.thumbnail__image}
                                        />
                                    ) : (
                                        <div className={styles.placeholder}>
                                            <img
                                                src={stationLogo}
                                                alt={video.snippet.title + "-thumbnail"}
                                                className={styles.thumbnail__image}
                                                height="100%"
                                            />
                                        </div>
                                    )}
                                    <p className={styles.thumbnail__title}>{video.snippet.title}</p>
                                </li>
                            ))}
                        <li className={styles.buttonWrapper}>
                            {!!youtubeLink && (
                                <FauxButton
                                    kind={ButtonKind.SECONDARY}
                                    to={`https://www.youtube.com/${youtubeLink}`}
                                    size="small"
                                    target="_blank"
                                    className={styles.button}
                                >
                                    See All
                                </FauxButton>
                            )}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
});

export default YouTubePlayer;
